import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { DropdownContainer, DropdownSearchInput, StyledDropdown, VirtualDropdownList, dropdownListHeight } from './Dropdown.styles';
import { DropdownApplyButton } from './DropdownApplyButton';
import { DropdownButton } from './DropdownButton';
import { DropdownItem, getItemSize, itemTitle } from './DropdownItem';
import { NoResultDropdownItem, dropdownItemHeight } from './NoResultsDropdownItem';
import { LocalThemeProvider } from '../../../../play-core/styles/LocalThemeProvider';
import { equals } from '../../../../play-core/utils/array-helper';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const Dropdown = ({
  title,
  items,
  selectedItemIds,
  isLoading,
  onChange,
  isDisabled = false,
  showSelectedValues = false,
  theme,
  forceMinOneValue = false,
  disableSearch = false,
  disableSort = false,
  expand = false
}) => {
  const {
    t
  } = useTranslation();
  const dropdownRef = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(selectedItemIds);
  useClickAway(dropdownRef, () => setDropdownOpen(false));
  useEffect(() => setSelectedIds(selectedItemIds), [selectedItemIds]);
  const displayedTitle = showSelectedValues ? buildTitle(items, selectedIds, title, t) : title;
  const isSearchAvailable = items.length >= 5;
  const hasChanges = !equals(selectedItemIds, selectedIds);
  let filteredItems = items.filter(item => item.title && item.title.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  if (!disableSort) filteredItems = filteredItems.sort(item => selectedItemIds.includes(item.id) ? -1 : 0);
  const toggleSelection = id => {
    if (selectedIds.includes(id)) {
      if (forceMinOneValue && selectedIds.length <= 1) {
        return;
      }
      setSelectedIds(selectedIds.filter(selectedItem => selectedItem !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };
  const setDropdownOpen = shouldOpen => {
    if (isOpen) {
      setSearchTerm('');
      hasChanges && onChange(selectedIds);
    }
    setOpen(shouldOpen);
  };
  return /*#__PURE__*/_jsx(LocalThemeProvider, {
    type: theme,
    children: /*#__PURE__*/_jsxs(StyledDropdown, {
      ref: dropdownRef,
      expand: expand,
      children: [/*#__PURE__*/_jsx(DropdownButton, {
        title: displayedTitle,
        isOpen: isOpen,
        isDisabled: isDisabled,
        isLoading: isLoading,
        onClick: () => {
          if (!isDisabled) {
            setDropdownOpen(!isOpen);
          }
        }
      }), isOpen && !isLoading && /*#__PURE__*/_jsxs(DropdownContainer, {
        children: [!disableSearch && isSearchAvailable && /*#__PURE__*/_jsx(DropdownSearchInput, {
          searchTerm: searchTerm,
          onSearch: value => setSearchTerm(value)
        }), filteredItems.length === 0 ? /*#__PURE__*/_jsx(NoResultDropdownItem, {}) : /*#__PURE__*/_jsx(VirtualDropdownList, {
          itemCount: filteredItems.length,
          itemSize: index => getItemSize(t, filteredItems[index]),
          width: "100%",
          height: Math.min(dropdownListHeight, dropdownItemHeight * filteredItems.length),
          innerElementType: "ul",
          children: ({
            index,
            style
          }) => /*#__PURE__*/_jsx(DropdownItem, {
            style: style,
            item: filteredItems[index],
            isSelected: selectedIds.includes(filteredItems[index].id),
            onToggle: () => toggleSelection(filteredItems[index].id),
            searchTerm: searchTerm.trim()
          }, filteredItems[index].id)
        }, filteredItems.length), /*#__PURE__*/_jsx(DropdownApplyButton, {
          onClick: () => setDropdownOpen(false),
          hasChanges: hasChanges
        })]
      })]
    })
  });
};
const buildTitle = (items, selectedItemIds, title, t) => {
  if (selectedItemIds.length > 0) {
    return selectedItemIds.map(selectedId => items.find(item => item.id === selectedId)).map(item => itemTitle(t, item)).filter(_title => !!_title).join(', ') || title;
  }
  return title;
};